import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  IconButton,
  Image,
  Input,
  InputGroup,
  InputLeftAddon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Switch,
  Text,
  Textarea,
  Wrap,
  WrapItem,
  useToast,
} from '@chakra-ui/react';

export const FLAG_TYPES = [
  { key: 'stain', label: 'Stain', chargeLabel: 'stain treatment' },
  { key: 'tear', label: 'Tear / hole', chargeLabel: 'repair' },
  { key: 'missing_button', label: 'Missing button', chargeLabel: 'button replacement' },
  { key: 'color_bleed', label: 'Color bleed', chargeLabel: 'color treatment' },
  { key: 'other', label: 'Other damage', chargeLabel: 'repair' },
];

const MAX_PHOTOS = 3;

export function compressPhoto(file, maxDimension = 1400, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new window.Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(url);
      const ratio = Math.min(1, maxDimension / Math.max(img.width, img.height));
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

/**
 * Staff form to flag a stained / damaged item.
 * existingPhotos: [{ url, preview }] photos already stored for the order (url is sent back, preview is shown).
 * onSubmit(payload) must resolve to the created flag or throw an Error with a message.
 */
export default function FlagItemForm({ isOpen, onClose, defaultItem = '', existingPhotos = [], onSubmit }) {
  const toast = useToast();
  const cameraRef = useRef(null);
  const [flagType, setFlagType] = useState('stain');
  const [itemLabel, setItemLabel] = useState(defaultItem);
  const [location, setLocation] = useState('');
  const [note, setNote] = useState('');
  const [offerCharge, setOfferCharge] = useState(false);
  const [chargeAmount, setChargeAmount] = useState('');
  const [chargeLabel, setChargeLabel] = useState('stain treatment');
  const [labelTouched, setLabelTouched] = useState(false);
  const [newPhotos, setNewPhotos] = useState([]);
  const [picked, setPicked] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setFlagType('stain');
    setItemLabel(defaultItem);
    setLocation('');
    setNote('');
    setOfferCharge(false);
    setChargeAmount('');
    setChargeLabel('stain treatment');
    setLabelTouched(false);
    setNewPhotos([]);
    setPicked([]);
  }, [isOpen, defaultItem]);

  const chooseType = (key) => {
    setFlagType(key);
    if (!labelTouched) setChargeLabel(FLAG_TYPES.find((t) => t.key === key)?.chargeLabel || 'repair');
  };

  const photoCount = newPhotos.length + picked.length;

  const addPhoto = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const dataUrl = await compressPhoto(file);
      setNewPhotos((prev) => [...prev, dataUrl].slice(0, MAX_PHOTOS));
    } catch {
      toast({ title: 'Could not read that photo', status: 'error', duration: 3000 });
    }
  };

  const togglePicked = (url) => {
    setPicked((prev) => {
      if (prev.includes(url)) return prev.filter((u) => u !== url);
      if (prev.length + newPhotos.length >= MAX_PHOTOS) return prev;
      return [...prev, url];
    });
  };

  const amount = parseFloat(chargeAmount);
  const chargeValid = !offerCharge || (amount > 0 && amount <= 500);
  const canSend = itemLabel.trim() && photoCount > 0 && chargeValid && !saving;

  const submit = async () => {
    setSaving(true);
    try {
      const flag = await onSubmit({
        itemLabel: itemLabel.trim(),
        flagType,
        location: location.trim() || null,
        note: note.trim() || null,
        chargeAmount: offerCharge ? amount : null,
        chargeLabel: offerCharge ? chargeLabel.trim() || null : null,
        images: newPhotos,
        photoUrls: picked,
      });
      const via = flag?.notifiedVia;
      toast({
        title: offerCharge ? 'Sent for approval' : 'Flag saved',
        description: [
          via ? `Customer notified by ${via.replace('+', ' and ')}.` : 'The customer could not be texted or emailed. Please call them.',
          flag?.notifyNote,
        ].filter(Boolean).join(' '),
        status: via ? 'success' : 'warning',
        duration: 5000,
      });
      onClose();
    } catch (e) {
      toast({ title: 'Could not flag the item', description: e.message, status: 'error', duration: 5000 });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }} scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Flag stain or damage</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl mb={4}>
            <FormLabel fontSize="sm">What did you find?</FormLabel>
            <Wrap spacing={2}>
              {FLAG_TYPES.map((t) => (
                <WrapItem key={t.key}>
                  <Button
                    size="sm"
                    borderRadius="full"
                    variant={flagType === t.key ? 'solid' : 'outline'}
                    colorScheme={flagType === t.key ? 'orange' : 'gray'}
                    onClick={() => chooseType(t.key)}
                  >
                    {t.label}
                  </Button>
                </WrapItem>
              ))}
            </Wrap>
          </FormControl>

          <FormControl mb={3} isRequired>
            <FormLabel fontSize="sm">Item</FormLabel>
            <Input value={itemLabel} onChange={(e) => setItemLabel(e.target.value)} placeholder="White shirt" />
          </FormControl>

          <FormControl mb={3}>
            <FormLabel fontSize="sm">Where on the item</FormLabel>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="collar, left sleeve…" />
          </FormControl>

          <FormControl mb={4} isRequired>
            <FormLabel fontSize="sm">Photos ({photoCount}/{MAX_PHOTOS})</FormLabel>
            <SimpleGrid columns={3} spacing={2}>
              {newPhotos.map((src, i) => (
                <Box key={`new-${i}`} position="relative">
                  <Image src={src} alt={`Close-up ${i + 1}`} h="90px" w="100%" objectFit="cover" borderRadius="md" />
                  <IconButton
                    aria-label="Remove photo"
                    icon={<Text fontSize="xs">✕</Text>}
                    size="xs"
                    colorScheme="red"
                    borderRadius="full"
                    position="absolute"
                    top={1}
                    right={1}
                    onClick={() => setNewPhotos((prev) => prev.filter((_, j) => j !== i))}
                  />
                </Box>
              ))}
              {photoCount < MAX_PHOTOS && (
                <Button h="90px" variant="outline" borderStyle="dashed" onClick={() => cameraRef.current?.click()}>
                  📷 Close-up
                </Button>
              )}
            </SimpleGrid>
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              style={{ display: 'none' }}
              onChange={addPhoto}
            />
            {existingPhotos.length > 0 && (
              <>
                <Text fontSize="xs" color="gray.500" mt={3} mb={1}>Or use a photo you already took:</Text>
                <SimpleGrid columns={4} spacing={2}>
                  {existingPhotos.map((p) => (
                    <Image
                      key={p.url}
                      src={p.preview || p.url}
                      alt="Order photo"
                      h="64px"
                      w="100%"
                      objectFit="cover"
                      borderRadius="md"
                      cursor="pointer"
                      borderWidth={3}
                      borderColor={picked.includes(p.url) ? 'orange.400' : 'transparent'}
                      opacity={picked.includes(p.url) ? 1 : 0.7}
                      onClick={() => togglePicked(p.url)}
                    />
                  ))}
                </SimpleGrid>
              </>
            )}
          </FormControl>

          <FormControl mb={3}>
            <FormLabel fontSize="sm">Note (optional)</FormLabel>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Looks like red wine. We'll pre-treat it."
            />
          </FormControl>

          <Box borderWidth="1px" borderRadius="md" p={3}>
            <HStack justify="space-between">
              <Box>
                <Text fontWeight="semibold" fontSize="sm">Offer a paid treatment</Text>
                <Text fontSize="xs" color="gray.500">
                  The customer approves or declines from the text. Approved charges are added to the order.
                </Text>
              </Box>
              <Switch isChecked={offerCharge} onChange={(e) => setOfferCharge(e.target.checked)} />
            </HStack>
            {offerCharge && (
              <HStack mt={3} align="flex-end">
                <FormControl flex={2}>
                  <FormLabel fontSize="xs">For</FormLabel>
                  <Input
                    size="sm"
                    value={chargeLabel}
                    onChange={(e) => { setChargeLabel(e.target.value); setLabelTouched(true); }}
                  />
                </FormControl>
                <FormControl flex={1} isInvalid={!chargeValid}>
                  <FormLabel fontSize="xs">Amount</FormLabel>
                  <InputGroup size="sm">
                    <InputLeftAddon>$</InputLeftAddon>
                    <Input
                      type="number"
                      inputMode="decimal"
                      min="0"
                      step="0.5"
                      value={chargeAmount}
                      onChange={(e) => setChargeAmount(e.target.value)}
                      aria-label="Charge amount"
                    />
                  </InputGroup>
                </FormControl>
              </HStack>
            )}
          </Box>
        </ModalBody>
        <ModalFooter gap={2}>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button colorScheme="orange" onClick={submit} isDisabled={!canSend} isLoading={saving}>
            {offerCharge ? 'Send for approval' : 'Save & notify customer'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
